import { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { carregarConversoesPrincipais, type ConversaoPrincipal } from '../api/conversoes'
import { REDES, carregarSocial, nomeRede, type DadosSocial, type MetricasDia, type Publicacao, type Rede } from '../api/social'
import { AvisoConversao } from '../components/AvisoConversao'
import { ChartEmpty } from '../components/ChartEmpty'
import { ColunasDiarias, type PontoDiario } from '../components/charts/ColunasDiarias'
import { useCoresRede } from '../components/charts/coresPlataforma'
import { TabelaDiaria } from '../components/charts/TabelaDiaria'
import { KpiCard } from '../components/KpiCard'
import { LeituraIA } from '../components/LeituraIA'
import { PainelShell } from '../components/PainelShell'
import { useFiltros } from '../state/FiltrosContext'
import { formatarConversoes, formatarInteiro, formatarNumero, formatarPercentual, razao } from '../utils/formato'
import { diasDoIntervalo } from '../utils/periodo'

const ZERO: MetricasDia = { novosSeguidores: 0, alcance: 0, visualizacoes: 0, interacoes: 0, visitasPerfil: 0, cliquesLink: 0 }

const somar = (a: MetricasDia, b: MetricasDia): MetricasDia => ({
  novosSeguidores: a.novosSeguidores + b.novosSeguidores,
  alcance: a.alcance + b.alcance,
  visualizacoes: a.visualizacoes + b.visualizacoes,
  interacoes: a.interacoes + b.interacoes,
  visitasPerfil: a.visitasPerfil + b.visitasPerfil,
  cliquesLink: a.cliquesLink + b.cliquesLink,
})

const variacao = (atual: number | null, anterior: number | null) =>
  atual == null || anterior == null || anterior === 0 ? null : ((atual - anterior) / Math.abs(anterior)) * 100

const traco = (v: number | null, f: (n: number) => string) => (v == null ? '—' : f(v))

// Publicação mostrada no horário de São Paulo, qualquer que seja o fuso do navegador.
const dataHoraSP = new Intl.DateTimeFormat('pt-BR', {
  timeZone: 'America/Sao_Paulo',
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
})

export function RedesSociaisPage() {
  const { intervalo, marcaId, marcas } = useFiltros()
  const cores = useCoresRede()
  const [rede, setRede] = useState<Rede | ''>('')
  const [modo, setModo] = useState<'grafico' | 'tabela'>('grafico')
  const [dados, setDados] = useState<DadosSocial | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [principais, setPrincipais] = useState<ConversaoPrincipal[]>([])

  useEffect(() => {
    carregarConversoesPrincipais().then(setPrincipais).catch(() => setPrincipais([]))
  }, [])

  const filtro = useMemo(() => ({ intervalo, marcaId, rede }), [intervalo, marcaId, rede])

  useEffect(() => {
    let cancelado = false
    setCarregando(true)
    carregarSocial(filtro)
      .then((d) => {
        if (cancelado) return
        setDados(d)
        setErro(null)
      })
      .catch((e: Error) => {
        if (!cancelado) setErro(e.message)
      })
      .finally(() => {
        if (!cancelado) setCarregando(false)
      })
    return () => {
      cancelado = true
    }
  }, [filtro])

  const redes: Rede[] = rede ? [rede] : REDES.map((r) => r.id)

  // Um ponto por dia do intervalo, com o detalhe por rede; dia sem dado = zero.
  const dias = useMemo(() => {
    const porDia = new Map<string, { total: MetricasDia; porRede: Partial<Record<Rede, MetricasDia>> }>()
    for (const d of diasDoIntervalo(intervalo)) porDia.set(d, { total: ZERO, porRede: {} })
    for (const l of dados?.serie ?? []) {
      const dia = porDia.get(l.data)
      if (!dia) continue
      dia.total = somar(dia.total, l)
      dia.porRede[l.rede] = somar(dia.porRede[l.rede] ?? ZERO, l)
    }
    return [...porDia.entries()].map(([data, v]) => ({ data, ...v }))
  }, [dados, intervalo])

  const atual = dados?.atual
  const anterior = dados?.anterior
  const temDemo = Boolean(dados?.serie.some((l) => l.demo) || atual?.demo || anterior?.demo)
  const vazio = dados != null && dados.serie.length === 0 && dados.publicacoes.length === 0
  const nomeConversao = marcaId ? (principais.find((p) => p.marcaId === marcaId)?.nome ?? 'Conversões') : 'Conversões'
  const marcasNoRecorte = marcaId ? marcas.filter((m) => m.id === marcaId) : marcas
  // O GA4 não separa por rede: com uma rede no filtro, avisa que o número é das três.
  const notaSite = rede ? 'GA4 · das três redes (o GA4 não separa por rede)' : 'GA4 · canal Organic Social'

  const serieDe = (f: (m: MetricasDia) => number | null) => dias.map((d) => f(d.total) ?? 0)
  const pontos = (f: (m: MetricasDia) => number): PontoDiario[] =>
    dias.map((d) => ({ data: d.data, ...Object.fromEntries(redes.map((r) => [r, f(d.porRede[r] ?? ZERO)])) }))
  const series = redes.map((r) => ({ chave: r, nome: nomeRede(r), cor: cores[r] }))

  const kpis = [
    {
      label: 'Seguidores',
      valor: formatarNumero(atual?.seguidores ?? 0),
      delta: variacao(atual?.seguidores ?? null, anterior?.seguidores ?? null),
      hint: 'No fim do período',
    },
    {
      label: 'Novos seguidores',
      valor: formatarNumero(atual?.novosSeguidores ?? 0),
      delta: variacao(atual?.novosSeguidores ?? null, anterior?.novosSeguidores ?? null),
      spark: serieDe((m) => m.novosSeguidores),
    },
    {
      label: 'Visualizações',
      valor: formatarNumero(atual?.visualizacoes ?? 0),
      delta: variacao(atual?.visualizacoes ?? null, anterior?.visualizacoes ?? null),
      spark: serieDe((m) => m.visualizacoes),
    },
    {
      label: 'Interações',
      valor: formatarNumero(atual?.interacoes ?? 0),
      delta: variacao(atual?.interacoes ?? null, anterior?.interacoes ?? null),
      spark: serieDe((m) => m.interacoes),
    },
    {
      label: 'Taxa de engajamento',
      valor: traco(atual ? razao(atual.interacoes, atual.visualizacoes) : null, formatarPercentual),
      delta: variacao(
        atual ? razao(atual.interacoes, atual.visualizacoes) : null,
        anterior ? razao(anterior.interacoes, anterior.visualizacoes) : null,
      ),
      spark: serieDe((m) => razao(m.interacoes, m.visualizacoes)),
      hint: 'Interações por visualização',
    },
    {
      label: 'Publicações',
      valor: formatarInteiro(atual?.publicacoes ?? 0),
      delta: variacao(atual?.publicacoes ?? null, anterior?.publicacoes ?? null),
      melhorQuando: 'neutro' as const,
    },
    {
      label: 'Sessões no site vindas das redes',
      valor: formatarNumero(dados?.site.sessoes ?? 0),
      delta: variacao(dados?.site.sessoes ?? null, dados?.siteAnterior.sessoes ?? null),
      hint: notaSite,
    },
    {
      label: `${nomeConversao} vindas das redes`,
      valor: formatarConversoes(dados?.site.conversoes ?? 0),
      delta: variacao(dados?.site.conversoes ?? null, dados?.siteAnterior.conversoes ?? null),
      hint: notaSite,
    },
  ]

  const graficos = [
    { titulo: 'Novos seguidores', f: (m: MetricasDia) => m.novosSeguidores },
    { titulo: 'Interações', f: (m: MetricasDia) => m.interacoes },
  ]

  return (
    <PainelShell titulo="Redes sociais" fontes="Instagram · Facebook · TikTok · GA4">
      {/* Filtro do painel: uma linha só, acima de tudo o que ele recorta. */}
      <ToggleButtonGroup
        exclusive
        size="small"
        value={rede}
        onChange={(_, v: Rede | '' | null) => v !== null && setRede(v)}
        aria-label="Rede"
        sx={{ alignSelf: 'flex-start' }}
      >
        <ToggleButton value="" sx={{ textTransform: 'none', px: 1.5 }}>
          Todas as redes
        </ToggleButton>
        {REDES.map((r) => (
          <ToggleButton key={r.id} value={r.id} sx={{ textTransform: 'none', px: 1.5 }}>
            {r.nome}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      {temDemo ? (
        <Alert severity="warning" variant="outlined">
          <strong>Dados de demonstração.</strong> Números fictícios, só para testar o painel — saem quando os
          coletores estiverem ligados às contas reais.
        </Alert>
      ) : null}

      <AvisoConversao marcas={marcasNoRecorte} principais={principais} plataformasDaFonte={['ga4']} nomeDaFonte="do GA4" />

      {erro ? <Alert severity="error">Não foi possível carregar o painel: {erro}</Alert> : null}

      {vazio ? (
        <Paper variant="outlined" sx={{ pb: 6 }}>
          <ChartEmpty title="Sem dados das redes sociais neste recorte" />
        </Paper>
      ) : (
        // Ao trocar filtro, a tela anterior fica esmaecida em vez de piscar.
        <Stack spacing={2} sx={{ opacity: carregando && dados ? 0.55 : 1, transition: 'opacity 160ms ease' }}>
          <LeituraIA painel="redes_sociais" filtros={{ rede }} />

          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
            }}
          >
            {kpis.map((k) => (
              <KpiCard
                key={k.label}
                label={k.label}
                value={k.valor}
                deltaPct={k.delta}
                deltaSufixo="vs período anterior"
                melhorQuando={k.melhorQuando}
                sparkline={k.spark}
                hint={k.hint}
                loading={!dados}
              />
            ))}
          </Box>

          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2" color="text.secondary">
              Por dia
            </Typography>
            <ToggleButtonGroup exclusive size="small" value={modo} onChange={(_, v) => v && setModo(v)} aria-label="Visualização">
              <ToggleButton value="grafico" sx={{ textTransform: 'none', px: 1.5 }}>
                Gráfico
              </ToggleButton>
              <ToggleButton value="tabela" sx={{ textTransform: 'none', px: 1.5 }}>
                Tabela
              </ToggleButton>
            </ToggleButtonGroup>
          </Stack>

          {modo === 'grafico' ? (
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' } }}>
              {graficos.map((g) => (
                <ColunasDiarias
                  key={g.titulo}
                  titulo={g.titulo}
                  pontos={pontos(g.f)}
                  series={series}
                  formatar={formatarInteiro}
                  formatarEixo={formatarNumero}
                />
              ))}
            </Box>
          ) : (
            <TabelaDiaria
              colunas={series.map((s) => ({ chave: s.chave, nome: s.nome }))}
              series={graficos.map((g) => ({ titulo: g.titulo, pontos: pontos(g.f), formatar: formatarInteiro }))}
            />
          )}

          <Publicacoes publicacoes={dados?.publicacoes ?? []} mostrarMarca={!marcaId} />
        </Stack>
      )}
    </PainelShell>
  )
}

function Publicacoes({ publicacoes, mostrarMarca }: { publicacoes: Publicacao[]; mostrarMarca: boolean }) {
  const cores = useCoresRede()
  const num = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } as const
  const [modo, setModo] = useState<'cards' | 'tabela'>('cards')

  return (
    <Paper variant="outlined">
      <Stack direction="row" alignItems="baseline" justifyContent="space-between" sx={{ px: 2, pt: 2, pb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, fontFamily: '"Outfit", sans-serif' }}>
          Publicações
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Typography variant="caption" color="text.secondary">
            As {publicacoes.length} com mais interações no período
          </Typography>
          <ToggleButtonGroup exclusive size="small" value={modo} onChange={(_, v) => v && setModo(v)} aria-label="Exibição das publicações">
            <ToggleButton value="cards" sx={{ textTransform: 'none', px: 1.25, py: 0.25 }}>
              Cards
            </ToggleButton>
            <ToggleButton value="tabela" sx={{ textTransform: 'none', px: 1.25, py: 0.25 }}>
              Tabela
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Stack>
      {publicacoes.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ px: 2, pb: 2 }}>
          Nenhuma publicação neste recorte.
        </Typography>
      ) : modo === 'cards' ? (
        <Box sx={{ display: 'grid', gap: 2, px: 2, pb: 2, gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' } }}>
          {publicacoes.map((p) => (
            <CardPublicacao key={p.id} p={p} cor={cores[p.rede]} mostrarMarca={mostrarMarca} />
          ))}
        </Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Publicação</TableCell>
                <TableCell>Rede</TableCell>
                {mostrarMarca ? <TableCell>Marca</TableCell> : null}
                <TableCell>Quando</TableCell>
                <TableCell align="right">Visualizações</TableCell>
                <TableCell align="right">Interações</TableCell>
                <TableCell align="right">Engajamento</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {publicacoes.map((p) => (
                <TableRow key={p.id} hover>
                  <TableCell sx={{ maxWidth: 320 }}>
                    <Typography variant="body2" noWrap title={p.legenda ?? undefined}>
                      {p.link ? (
                        <Link href={p.link} target="_blank" rel="noopener noreferrer" underline="hover">
                          {p.legenda || 'Sem legenda'}
                        </Link>
                      ) : (
                        p.legenda || 'Sem legenda'
                      )}
                    </Typography>
                    {p.tipo ? (
                      <Typography variant="caption" color="text.secondary">
                        {p.tipo}
                      </Typography>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.75} alignItems="center">
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: cores[p.rede] }} aria-hidden />
                      <span>{nomeRede(p.rede)}</span>
                    </Stack>
                  </TableCell>
                  {mostrarMarca ? <TableCell sx={{ whiteSpace: 'nowrap' }}>{p.marca}</TableCell> : null}
                  <TableCell sx={num}>{dataHoraSP.format(new Date(p.publicadaEm))}</TableCell>
                  <TableCell align="right" sx={num}>
                    {formatarInteiro(p.visualizacoes)}
                  </TableCell>
                  <TableCell align="right" sx={num}>
                    {formatarInteiro(p.interacoes)}
                  </TableCell>
                  <TableCell align="right" sx={num}>
                    {traco(razao(p.interacoes, p.visualizacoes), formatarPercentual)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  )
}

/** Uma publicação em card: a miniatura (cópia guardada pela coleta), a legenda e os números. */
function CardPublicacao({ p, cor, mostrarMarca }: { p: Publicacao; cor: string; mostrarMarca: boolean }) {
  const [semImagem, setSemImagem] = useState(false)
  const engajamento = razao(p.interacoes, p.visualizacoes)
  const imagem = p.miniatura && !semImagem ? (
    <Box component="img" src={p.miniatura} alt="" loading="lazy" onError={() => setSemImagem(true)} sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
  ) : (
    <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', color: 'text.secondary', px: 2, textAlign: 'center' }}>
      <Typography variant="caption">Sem miniatura{p.miniatura ? '' : ' (aparece na próxima coleta)'}</Typography>
    </Stack>
  )
  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ position: 'relative', aspectRatio: '4 / 5', bgcolor: 'action.hover' }}>
        {p.link ? (
          <Link href={p.link} target="_blank" rel="noopener noreferrer" aria-label={`Abrir a publicação na rede: ${p.legenda ?? 'sem legenda'}`} sx={{ display: 'block', height: '100%' }}>
            {imagem}
          </Link>
        ) : (
          imagem
        )}
        {p.tipo ? (
          <Box sx={{ position: 'absolute', top: 8, left: 8, px: 1, py: 0.25, borderRadius: 1, bgcolor: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: 12, textTransform: 'capitalize' }}>
            {p.tipo}
          </Box>
        ) : null}
      </Box>
      <Stack spacing={1} sx={{ p: 1.5, flex: 1 }}>
        <Stack direction="row" spacing={0.75} alignItems="center" useFlexGap flexWrap="wrap">
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: cor }} aria-hidden />
          <Typography variant="caption">{nomeRede(p.rede)}</Typography>
          {mostrarMarca ? (
            <Typography variant="caption" color="text.secondary">
              · {p.marca}
            </Typography>
          ) : null}
          <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontVariantNumeric: 'tabular-nums' }}>
            {dataHoraSP.format(new Date(p.publicadaEm))}
          </Typography>
        </Stack>
        <Typography
          variant="body2"
          title={p.legenda ?? undefined}
          sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', minHeight: '2.86em' }}
        >
          {p.legenda || 'Sem legenda'}
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1, mt: 'auto', pt: 0.5 }}>
          {[
            ['Visualizações', formatarInteiro(p.visualizacoes)],
            ['Interações', formatarInteiro(p.interacoes)],
            ['Engajamento', traco(engajamento, formatarPercentual)],
          ].map(([rotulo, valor]) => (
            <Box key={rotulo} sx={{ minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                {rotulo}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                {valor}
              </Typography>
            </Box>
          ))}
        </Box>
      </Stack>
    </Paper>
  )
}
